import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TopNavigation, TOP_NAVIGATION_HEIGHT } from '@framework/ui/elements/TopNavigation';
import { WorkspaceEditModal } from './internal/WorkspaceEditModal';
import { WorkspaceDeleteConfirmModal } from './internal/WorkspaceDeleteConfirmModal';
import { WorkspaceEditButton } from './internal/WorkspaceEditButton';
import { MemberSummary } from '@user/pages/WorkspacePage/internal/MemberSummary';
import { Description } from '@user/pages/WorkspacePage/internal/Description';
import { PageTitleManager } from '@user/pages/PageTitle/PageTitleManager';
import { ViewModelRootFolder } from '@workspace/view-model-folder/ui/ViewModelRootFolder';
import type { Workspace } from '@workspace/domain/workspace/Workspace';
import { usePageViewLogger } from '@framework/action-log';
import { ShareSetting } from './internal/ShareSetting';
import { SidebarOpenButton, useSidebarContext } from '@user/Sidebar';
import { WorkspaceMemberRole } from '@workspace/domain/workspace';
import { usePrimitiveValueListener } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import type { WorkspaceInternalPublicMemberRoleJSON, UserId, FolderId } from 'balus-common';
import { useParams } from 'react-router';
import { WorkspacePublicStatusIcon } from '@workspace/ui';
import { useWorkspaceMemberRoles } from '@workspace/domain/workspace/useWorkspaceMemberRoles';

const PersonalWorkspaceDescription: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="flex w-full flex-col">
      <div className="mb-1 flex flex-row items-center justify-between border-b border-gray-500">
        <div className="text-2xl font-bold">{t('workspace.personalWorkspace.sharingTitle')}</div>
      </div>

      <div>
        <div className="pt-1 font-bold">{t('workspace.personalWorkspace.title')}</div>
        <div className="pt-1 text-sm">
          {t('workspace.personalWorkspace.description1')}
          <br />
          {t('workspace.personalWorkspace.description2')}
        </div>
      </div>
    </div>
  );
};

const useWorkspacePageView = (workspace: Workspace | null): void => {
  const pageViewLogger = usePageViewLogger('WorkspacePage');

  useEffect(() => {
    if (!workspace) return;

    pageViewLogger.landing({
      workspaceId: workspace.id,
      workspaceName: workspace.name.value,
    });
    return () => pageViewLogger.leaving();
  }, [workspace, pageViewLogger]);
};

type ModalTarget = 'Edit' | 'Delete';

type Props = {
  workspace: Workspace;
  currentUserId: UserId;
};

export const WorkspacePage: React.FC<Props> = ({ workspace, currentUserId }: Props) => {
  useWorkspacePageView(workspace);

  const { isSidebarOpenButtonVisible, toggleSidebar } = useSidebarContext();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { folderId } = useParams<{ folderId?: FolderId }>();

  // フォルダ切り替え時にスクロール位置をリセット
  useEffect(() => {
    // DOMの更新が完了した後にスクロール位置をリセット
    const frameId = requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    });

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [folderId]);

  useEffect(() => {
    const pageTitleManager = new PageTitleManager();
    pageTitleManager.setWorkspacePageTitle(workspace.name);
    return () => {
      pageTitleManager.setDefaultPageTitle();
    };
  }, [workspace]);

  const [internalPublicSetting] = usePrimitiveValueListener<WorkspaceInternalPublicMemberRoleJSON>(
    RTDBPath.Group.internalPublicWorkspacePath(workspace.groupId, workspace.id)
  );

  const [groupEnabledIndividualViewModelPermissions] = usePrimitiveValueListener<boolean>(
    RTDBPath.Group.enableIndividualViewModelPermissionsPath(workspace.groupId)
  );

  const isEditable = useWorkspaceMemberRoles(workspace.id)?.getRoleOf(currentUserId) === WorkspaceMemberRole.admin;

  const [modalTarget, setModalTarget] = useState<ModalTarget | null>(null);

  const showEditModal = useCallback(() => setModalTarget('Edit'), []);
  const showDeleteModal = useCallback(() => setModalTarget('Delete'), []);
  const closeModal = useCallback(() => setModalTarget(null), []);

  // ロード完了までnullを返す
  if (groupEnabledIndividualViewModelPermissions === null) return null;

  return (
    <div className="block h-screen w-full overflow-y-auto">
      <TopNavigation>
        {isSidebarOpenButtonVisible && (
          <div className="size-10">
            <SidebarOpenButton onClick={toggleSidebar} />
          </div>
        )}
      </TopNavigation>
      <div
        ref={scrollContainerRef}
        className="overflow-y-auto px-10 pb-32 pt-3"
        style={{ height: `calc(100vh - ${TOP_NAVIGATION_HEIGHT}px)` }}
      >
        <div className="my-2 flex w-full items-center justify-between border-b-2 p-1">
          <div className="flex flex-row items-center justify-start" style={{ width: 'calc(100% - 8rem)' }}>
            {workspace.imageUrl && (
              <>
                <div className="inline-block size-8 overflow-hidden rounded-full">
                  <img className="size-full object-cover" src={workspace.imageUrl} alt={workspace.name.value} />
                </div>
                <div className="pr-2" />
              </>
            )}
            <div className="flex justify-start truncate text-3xl font-bold">{workspace.name.value}</div>
            <div className="flex px-2 text-2xl">
              <WorkspacePublicStatusIcon
                isPublicSpace={workspace.isPublicSpace}
                isPersonal={workspace.isPersonal}
                internalPublicSetting={internalPublicSetting}
              />
            </div>
          </div>
          {isEditable && currentUserId && (
            <div className="flex items-end justify-end">
              <WorkspaceEditButton onEdit={showEditModal} onDelete={showDeleteModal} />

              {modalTarget === 'Edit' && (
                <WorkspaceEditModal isOpen={true} onClose={closeModal} workspace={workspace} />
              )}

              {modalTarget === 'Delete' && (
                <WorkspaceDeleteConfirmModal
                  isOpen={true}
                  onClose={closeModal}
                  workspace={workspace}
                  shouldMoveToGroupPage={true}
                />
              )}
            </div>
          )}
        </div>

        <div className="flex w-full flex-row justify-between py-4">
          <div className="flex w-3/4 flex-col">
            <ViewModelRootFolder currentUserId={currentUserId} workspace={workspace} />
          </div>
          <div className="flex w-1/4 flex-col">
            {workspace.isPersonal ? (
              <PersonalWorkspaceDescription />
            ) : (
              <>
                <Description workspace={workspace} isEditable={isEditable} />
                <div className="pt-4" />
                <ShareSetting
                  workspace={workspace}
                  internalPublicSetting={internalPublicSetting}
                  isEditable={isEditable}
                  groupEnabledIndividualViewModelPermissions={groupEnabledIndividualViewModelPermissions}
                />
                <div className="pt-4" />
                <MemberSummary workspace={workspace} isEditable={isEditable} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
